import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

interface PromptOptions {
  title: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  required?: boolean;
}

interface InputDialogContextType {
  prompt: (options: PromptOptions) => Promise<string | null>;
}

const InputDialogContext = createContext<InputDialogContextType | null>(null);

export const useInputDialog = (): InputDialogContextType => {
  const ctx = useContext(InputDialogContext);
  if (!ctx) throw new Error('useInputDialog must be used within InputDialogProvider');
  return ctx;
};

interface DialogState extends PromptOptions {
  open: boolean;
}

export const InputDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [dialog, setDialog] = useState<DialogState>({
    open: false,
    title: '',
    message: '',
  });
  const [value, setValue] = useState('');
  const resolveRef = useRef<((value: string | null) => void) | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const prompt = useCallback((options: PromptOptions): Promise<string | null> => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setValue(options.defaultValue || '');
      setDialog({ ...options, open: true });
    });
  }, []);

  useEffect(() => {
    if (dialog.open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [dialog.open]);

  const handleClose = useCallback((result: string | null) => {
    setDialog((prev) => ({ ...prev, open: false }));
    resolveRef.current?.(result);
    resolveRef.current = null;
    setValue('');
  }, []);

  const handleSubmit = useCallback(() => {
    if (dialog.required && !value.trim()) return;
    handleClose(value);
  }, [value, dialog.required, handleClose]);

  return (
    <InputDialogContext.Provider value={{ prompt }}>
      {children}
      {dialog.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50 transition-opacity"
            onClick={() => handleClose(null)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="input-dialog-title"
            aria-describedby="input-dialog-message"
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden"
          >
            <div className="p-6">
              <h3 id="input-dialog-title" className="text-lg font-semibold text-gray-900 dark:text-white">
                {dialog.title}
              </h3>
              <p id="input-dialog-message" className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {dialog.message}
              </p>
              <textarea
                ref={inputRef}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                  if (e.key === 'Escape') handleClose(null);
                }}
                placeholder={dialog.placeholder}
                rows={3}
                className="mt-4 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
              {dialog.required && !value.trim() && (
                <p className="mt-1 text-xs text-red-500">This field is required</p>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => handleClose(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                {dialog.cancelText || 'Cancel'}
              </button>
              <button
                onClick={handleSubmit}
                disabled={dialog.required && !value.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {dialog.confirmText || 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </InputDialogContext.Provider>
  );
};
